/**
 * Display formatting.
 *
 * Those are the figures of what the product SHOWS. A field the member types
 * into is the other register: its value is Latin digits, with a plain full
 * stop for the decimal, because that value is what the API stores and what
 * every `\d` in a validator has to match. `latin: true` on the formatters
 * below is that register, `toLatinDigits` is the way in, and the kit's
 * `numeric` input applies it to every keystroke.
 */

import { JALALI_MONTHS, toJalali, zonedParts } from './jalali'

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
export const DECIMAL_MARK = '٫'
export const THOUSANDS_MARK = ','
export const PERCENT_MARK = '٪'

/**
 * What a missing number renders as. A hyphen, never a dash of any length:
 * an em dash is the single loudest typographic tell in a Persian document.
 */
export const EMPTY_VALUE = '-'

/** Latin digits to Persian digits. Leaves everything else alone. */
export function toPersianDigits(input: string | number): string {
  return String(input).replace(/[0-9]/g, (d) => PERSIAN_DIGITS[Number(d)])
}

/** Persian or Arabic digits to Latin digits, for parsing user input. */
export function toLatinDigits(input: string): string {
  return input
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/٫/g, '.')
    .replace(/٬/g, '')
}

export interface NumberOptions {
  /** Fixed number of decimals. Omitted means "as many as the value has, max 2". */
  decimals?: number
  /** Group thousands. Default true. */
  group?: boolean
  /** Render with Latin digits, for inputs and machine contexts. */
  latin?: boolean
}

/** A number as the product writes it. */
export function formatNumber(value: number | null | undefined, opts: NumberOptions = {}): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_VALUE
  const { decimals, group = true, latin = false } = opts
  const abs = Math.abs(value)
  const fixed = decimals !== undefined ? abs.toFixed(decimals) : trimDecimals(abs)
  const [intPart, fracPart] = fixed.split('.')
  const grouped = group ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, THOUSANDS_MARK) : intPart
  let out = grouped
  if (fracPart) out += (latin ? '.' : DECIMAL_MARK) + fracPart
  if (value < 0) out = '-' + out
  return latin ? out : toPersianDigits(out)
}

function trimDecimals(n: number): string {
  if (Number.isInteger(n)) return String(n)
  return String(Math.round(n * 100) / 100)
}

/** A Rial amount. The unit is rendered by the caller, usually as a .unit span. */
export function formatRial(value: number | null | undefined): string {
  return formatNumber(value, { decimals: 0 })
}

/** A weight in kilograms. */
export function formatWeight(value: number | null | undefined): string {
  return formatNumber(value, { decimals: 0 })
}

/** A percentage, sign first in logical order so bidi renders it correctly. */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_VALUE
  return formatNumber(Math.abs(value), { decimals }) + PERCENT_MARK
}

/** A signed delta, as the ticker writes it: ۲٫۴٪ + */
export function formatDelta(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_VALUE
  const sign = value > 0 ? '+' : value < 0 ? '-' : ''
  return `${formatPercent(value, decimals)} ${sign}`.trimEnd()
}

/** ۱۴۰۵/۰۶/۱۳, or 1405/06/13 with `latin`, which is what a date field carries. */
export function formatJalaliDate(
  date: Date | string | number | null | undefined,
  opts: { latin?: boolean } = {},
): string {
  const d = asDate(date)
  if (!d) return opts.latin ? '' : EMPTY_VALUE
  const { jy, jm, jd } = toJalali(d)
  const out = `${jy}/${pad(jm)}/${pad(jd)}`
  return opts.latin ? out : toPersianDigits(out)
}

/** ۱۳ شهریور ۱۴۰۵ */
export function formatJalaliLong(date: Date | string | number | null | undefined): string {
  const d = asDate(date)
  if (!d) return EMPTY_VALUE
  const { jy, jm, jd } = toJalali(d)
  return `${toPersianDigits(jd)} ${JALALI_MONTHS[jm - 1]} ${toPersianDigits(jy)}`
}

/** ۱۰:۰۵ or ۱۰:۰۵:۳۰ */
export function formatTime(
  date: Date | string | number | null | undefined,
  withSeconds = false,
): string {
  const d = asDate(date)
  if (!d) return EMPTY_VALUE
  const p = zonedParts(d)
  const base = `${pad(p.hour)}:${pad(p.minute)}`
  return toPersianDigits(withSeconds ? `${base}:${pad(p.second)}` : base)
}

/** ۱۴۰۵/۰۶/۱۳ ساعت ۱۰:۰۵ */
export function formatDateTime(date: Date | string | number | null | undefined): string {
  const d = asDate(date)
  if (!d) return EMPTY_VALUE
  return `${formatJalaliDate(d)} ساعت ${formatTime(d)}`
}

/** A duration in seconds as ۰۲:۱۴:۳۰, the validity clock format. */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return toPersianDigits(`${pad(h)}:${pad(m)}:${pad(sec)}`)
}

/**
 * When something happened, the way a notification list says it, the way
 * Divar and Digikala say it: «لحظاتی پیش» inside the first minute, «۵ دقیقه
 * پیش» inside the hour, «۲ ساعت پیش» for the rest of the same day, and the
 * Jalali date, ۱۴۰۵/۰۶/۱۲, for anything from an earlier day. Never a bare
 * clock time.
 *
 * The day is Tehran's, and it is checked first: a notice from 23:55 read at
 * 00:40 is yesterday's and shows its date, not «۴۵ دقیقه پیش». A stamp a
 * little ahead of this clock is skew and reads «لحظاتی پیش».
 *
 * `nowIso` is for tests and for a page that renders against the market
 * clock; it defaults to this instant. The API's notification list prints its
 * `atFa` by the same rule.
 */
export function formatRelativeFa(
  iso: Date | string | number | null | undefined,
  nowIso?: Date | string | number | null,
): string {
  const d = asDate(iso)
  if (!d) return EMPTY_VALUE
  const now = asDate(nowIso) ?? new Date()
  if (!sameTehranDay(d, now)) return formatJalaliDate(d)
  const seconds = Math.max(0, Math.round((now.getTime() - d.getTime()) / 1000))
  if (seconds < 60) return 'لحظاتی پیش'
  if (seconds < 3600) return `${toPersianDigits(Math.floor(seconds / 60))} دقیقه پیش`
  return `${toPersianDigits(Math.floor(seconds / 3600))} ساعت پیش`
}

/**
 * How long something has waited, as a queue's «مدت انتظار» column reads it:
 * «هم‌اکنون»، «۱۲ دقیقه»، «۵ ساعت»، «۳ روز». Measured against the
 * caller's clock, so a server on the business clock passes its own `now`.
 */
export function formatWaitFa(
  since: Date | string | number | null | undefined,
  now: Date | string | number,
): string {
  const d = asDate(since)
  const n = asDate(now)
  if (!d || !n) return EMPTY_VALUE
  const minutes = Math.max(0, Math.floor((n.getTime() - d.getTime()) / 60_000))
  if (minutes < 1) return 'هم‌اکنون'
  if (minutes < 60) return `${toPersianDigits(minutes)} دقیقه`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${toPersianDigits(hours)} ساعت`
  return `${toPersianDigits(Math.floor(hours / 24))} روز`
}

function sameTehranDay(a: Date, b: Date): boolean {
  const x = toJalali(a)
  const y = toJalali(b)
  return x.jy === y.jy && x.jm === y.jm && x.jd === y.jd
}

/** A national id or registration number, grouped for reading. */
export function formatNationalId(value: string): string {
  return toPersianDigits(toLatinDigits(value))
}

/** A mobile number as ۰۹۱۲ ۳۴۵ ۶۷۸۹ */
export function formatMobile(value: string): string {
  const raw = toLatinDigits(value).replace(/\D/g, '')
  const m = raw.match(/^(\d{4})(\d{3})(\d{4})$/)
  return toPersianDigits(m ? `${m[1]} ${m[2]} ${m[3]}` : raw)
}

/** Masked mobile for confirmation screens: ۰۹۱۲ ••• ۶۷۸۹ */
export function maskMobile(value: string): string {
  const raw = toLatinDigits(value).replace(/\D/g, '')
  const m = raw.match(/^(\d{4})(\d{3})(\d{4})$/)
  if (!m) return toPersianDigits(raw)
  return `${toPersianDigits(m[1])} ••• ${toPersianDigits(m[3])}`
}

/** Parse a user typed number that may carry Persian digits and separators. */
export function parseNumber(input: string): number | null {
  const cleaned = toLatinDigits(input).replace(/[,\s٬]/g, '')
  if (cleaned === '' || cleaned === '-') return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function asDate(v: Date | string | number | null | undefined): Date | null {
  if (v === null || v === undefined) return null
  const d = v instanceof Date ? v : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}
